// utils/loginPolicy.js
//
// Each hospital's login/registration policy, as served per tenant by
// GET /tenants/public → tenants[].loginPolicy:
//   { mode: 'email'|'mobile'|'both'|'either',
//     email:  { required, otp },
//     mobile: { required, otp },
//     anyOne?: true }            // only 'either' carries it
//
// The Super Admin sets it per tenant (Tenants → Features). The login,
// forgot-password and registration pages shape their fields from the
// SELECTED tenant's policy; with no tenant chosen (or a stale cached list)
// everything falls back to DEFAULT_POLICY, which is the PLATFORM's own answer
// — mobile-first since the owner's rule of 18 Sep 2026.
//
// 'either' is ANY ONE CONTACT, VERIFIED (owner, 17 Sep 2026): neither field is
// mandatory on its own, at least one must be filled, and one of the filled ones
// must carry an OTP proof — the other may stay blank OR filled and unverified.
// Because `email.otp` and `mobile.otp` are BOTH true in that mode, reading them
// per channel says "verify both", which is the bug this mode exists to fix. So
// the two questions a form actually asks are answered here, once, and the web
// app, the Super Admin console and the Pro-App all ask them the same way —
// mirroring Backend/utils/loginIdentifier#checkIdentifierPolicy, which is what
// actually decides.

// THE FALLBACK IS THE PLATFORM DEFAULT, NOT A HISTORIC ONE (owner, 18 Sep
// 2026). Every MOC patient carries a MOBILE NUMBER, mandatory and OTP-verified;
// an e-mail is OPTIONAL and OTP-verified only when it is given. That is the
// `features` row every tenant without an override inherits
// (Backend/utils/loginIdentifier#deriveLoginPolicy('mobile', false, true)), so
// a form that cannot see the tenant list must fall back to it rather than to
// the e-mail-mode rule the platform no longer runs — otherwise a stale cache
// stars the wrong field and lets a patient through with no number.
//
// It is FROZEN two levels deep: one page mutating the fallback would change
// what every other page demands, on a value that is shared by reference.
export const DEFAULT_POLICY = Object.freeze({
  mode: 'mobile',
  email: Object.freeze({ required: false, otp: true }),
  mobile: Object.freeze({ required: true, otp: true }),
});

// The selected tenant's policy out of the /tenants/public list, defensively —
// an old backend without loginPolicy, or no selection yet, yields the default.
export const policyFor = (tenants, tenantId) => {
  const tn = (tenants || []).find((x) => x.tenantId === tenantId);
  const p = tn?.loginPolicy;
  if (!p || !p.email || !p.mobile) return DEFAULT_POLICY;
  return p;
};

// "Any one contact, verified" — true only for the 'either' mode. `anyOne` is
// what the server sends; the mode string is the fallback for a backend that
// sends the mode without the flag.
export const isAnyOneContact = (policy) =>
  !!(policy && (policy.anyOne === true || policy.mode === 'either'));

// QUESTION 1 — "is a contact missing?" Only 'either' can answer yes on an
// otherwise valid form: the other modes star their mandatory fields and the
// per-field validators already refuse a blank one.
export const missingAnyContact = (policy, { hasEmail, hasPhone }) =>
  isAnyOneContact(policy) && !hasEmail && !hasPhone;

// QUESTION 2 — "does this form still owe an OTP?" (the desk's
// create/book-as-pending confirmation). Under 'either' ONE verified contact
// settles it, whatever the other one does; under the other modes every
// mandated-OTP channel that is filled must be verified, exactly as before.
export const otpVerificationPending = (policy, { hasEmail, hasPhone, emailVerified, phoneVerified }) => {
  const p = policy && policy.email && policy.mobile ? policy : DEFAULT_POLICY;
  if (isAnyOneContact(p)) return !((hasEmail && emailVerified) || (hasPhone && phoneVerified));
  return !!((p.email.otp && hasEmail && !emailVerified) || (p.mobile.otp && hasPhone && !phoneVerified));
};

// ---- The Mitra One Care PATIENT rule (owner, 2 Oct 2026) -------------------
// A patient of a CONNECTED tenant is a Mitra One Care patient, and patients
// come from India and abroad, so the account is never tied to an Indian
// mobile: AT LEAST ONE contact (e-mail or mobile), both allowed, neither
// mandatory on its own. That is no longer the hospital's staff login policy —
// GET /tenants/public serves it per tenant as `patientPolicy` (this object for
// a connected tenant, the tenant's own loginPolicy for an isolated one, which
// keeps its old rules) and GET /moc-auth/policy serves it on its own.
//
// WHO VERIFIES depends on who fills the form. A patient registering
// themselves proves every contact they give, on the form. A desk (or the
// Super Admin) creating the account proves nothing: the patient verifies their
// contacts and chooses a password at their first sign-in (the activation
// door), and the desk's optional "Send OTP" only spares them one step. So the
// connected desk forms ask the presence question below and never the
// pending-verification one.
//
// Frozen two levels deep, for the same reason DEFAULT_POLICY is.
export const MOC_PATIENT_POLICY = Object.freeze({
  mode: 'either',
  anyOne: true,
  email: Object.freeze({ required: false, otp: true }),
  mobile: Object.freeze({ required: false, otp: true }),
});

// Is this tenant CONNECTED? The /tenants/public row says so explicitly;
// `fallback` answers only when the row cannot (no list yet, an old backend
// without connectionMode) — the caller's own cached answer, never a guess made
// here.
export const isConnectedTenant = (tenants, tenantId, fallback = false) => {
  const mode = (tenants || []).find((x) => x.tenantId === tenantId)?.connectionMode;
  if (mode === 'connected' || mode === 'isolated') return mode === 'connected';
  return fallback === true;
};

// The PATIENT rule for this tenant: the row's patientPolicy; failing that, the
// MOC rule for a connected tenant and the tenant's own login policy for an
// isolated one — the same answer the server would have given.
export const patientPolicyFor = (tenants, tenantId, connectedFallback = false) => {
  const p = (tenants || []).find((x) => x.tenantId === tenantId)?.patientPolicy;
  if (p && p.email && p.mobile) return p;
  return isConnectedTenant(tenants, tenantId, connectedFallback)
    ? MOC_PATIENT_POLICY
    : policyFor(tenants, tenantId);
};

// ---- Backend login-policy errors -------------------------------------------
// The create endpoints answer 400 {code:'FIELD_REQUIRED'|'OTP_PROOF_REQUIRED',
// field, message:{en,fr}} when a tenant's policy is not satisfied, and
// 400 {code:'IDENTIFIER_REQUIRED', field:'email'} when a Mitra One Care
// patient arrives with no contact at all (owner, 2 Oct 2026). Map the code to
// a translated string (keys in the top-level `loginPolicy` locale section);
// fall back to the server's own message. Returns null when the payload is not
// one of these errors, so callers keep their generic handling.
const POLICY_ERROR_KEYS = {
  FIELD_REQUIRED: {
    email: 'loginPolicy.field_required_email',
    phone: 'loginPolicy.field_required_phone',
  },
  OTP_PROOF_REQUIRED: {
    email: 'loginPolicy.otp_proof_required_email',
    phone: 'loginPolicy.otp_proof_required_phone',
  },
  IDENTIFIER_REQUIRED: {
    email: 'loginPolicy.one_contact_required',
    phone: 'loginPolicy.one_contact_required',
  },
};

// 'phone' / 'phoneNo' / 'mobile' all mean the mobile field.
export const policyErrorField = (field) =>
  ['phone', 'phoneNo', 'phone_no', 'mobile'].includes(field) ? 'phone' : 'email';

// `policy` is optional: under an any-one-contact rule a FIELD_REQUIRED on the
// e-mail means "no contact at all" (the server parks that refusal on the first
// field), and "E-mail is required" would send the desk after the wrong field.
export const policyErrorMessage = (data, t, lang = 'en', policy = null) => {
  if (!data || typeof data !== 'object') return null;
  const byField = POLICY_ERROR_KEYS[data.code];
  if (!byField) return null;
  const serverMsg =
    (lang === 'fr' ? data.message?.fr : data.message?.en) ||
    data.message?.en || data.message?.fr ||
    (typeof data.message === 'string' ? data.message : '');
  const noContact = data.code === 'FIELD_REQUIRED' && isAnyOneContact(policy)
    && (!data.field || data.field === 'email');
  const key = noContact
    ? POLICY_ERROR_KEYS.IDENTIFIER_REQUIRED.email
    : byField[policyErrorField(data.field)];
  return t(key, {
    defaultValue: serverMsg || t('loginPolicy.field_required_generic', 'A required field is missing'),
  });
};
